import { useState, useRef, useEffect } from "react";
import "./EditCoverPhoto.css";
import { userprofileImage } from "../../api/auth";
import Cookies from "js-cookie";
import { Snackbar, Alert, CircularProgress } from '@mui/material';
import { setUserDetails } from "../../Redux/Slices/userDetailsSlice";
import { useSelector, useDispatch } from "react-redux";
import { getToken, getUserId } from "../../api/index";

function EditCoverPhoto() {
    const [selectedFile, setSelectedFile] = useState(null);
    const userDetails = useSelector((state) => state.userDetails.userDetails);
    const [previewUrl, setPreviewUrl] = useState(userDetails?.photos);
    const [previewImgError, setPreviewImgError] = useState(false);
    const videoRef = useRef(null);
    const [stream, setStream] = useState(null);
    const [hasPermission, setHasPermission] = useState(false);
    const dispatch = useDispatch();
    const [error, setError] = useState("");
    const [showCamera, setShowCamera] = useState(false);
    const [capturedImage, setCapturedImage] = useState(null);
    const [uploadLoading, setUploadLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });

    useEffect(() => {
        setPreviewUrl(userDetails?.photos);
        setPreviewImgError(false);
    }, [userDetails?.photos]);

    // Helper function to get user initials
    const getUserInitials = (firstName, lastName) => {
        const firstInitial = firstName ? firstName.charAt(0).toUpperCase() : '';
        const lastInitial = lastName ? lastName.charAt(0).toUpperCase() : '';
        return firstInitial + lastInitial;
    };

    // Helper function to generate background color based on initials
    const getBackgroundColor = (initials) => {
        const colors = [
            '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4',
            '#FFEAA7', '#DDA0DD', '#98D8C8', '#F7DC6F',
            '#BB8FCE', '#85C1E9', '#F8C471', '#82E0AA'
        ];

        let hash = 0;
        for (let i = 0; i < initials.length; i++) {
            hash = initials.charCodeAt(i) + ((hash << 5) - hash);
        }
        hash = Math.abs(hash);
        return colors[hash % colors.length];
    };

    const startCamera = async () => {
        if (!videoRef.current) return;

        try {
            const mediaStream = await navigator.mediaDevices.getUserMedia({
                video: {
                    width: { ideal: 1280 },
                    height: { ideal: 720 },
                    facingMode: "user"
                }
            });
            setStream(mediaStream);
            setHasPermission(true);
            setShowCamera(true);
            videoRef.current.srcObject = mediaStream;

            videoRef.current.onloadedmetadata = () => {
                videoRef.current.play();
            };
        } catch (err) {
            console.error("Camera access error:", err);
            setError("Camera access denied or not available.");
            setHasPermission(false);
        }
    };

    const stopCamera = () => {
        if (stream) {
            stream.getTracks().forEach((track) => track.stop());
        }
        setShowCamera(false);
        setCapturedImage(null);
    };

    const capturePhoto = () => {
        if (!videoRef.current) return;

        const canvas = document.createElement('canvas');
        canvas.width = videoRef.current.videoWidth;
        canvas.height = videoRef.current.videoHeight;
        const ctx = canvas.getContext('2d');

        // Flip the image horizontally to match the mirrored video
        ctx.scale(-1, 1);
        ctx.drawImage(videoRef.current, -canvas.width, 0, canvas.width, canvas.height);

        const imageDataUrl = canvas.toDataURL('image/jpeg');
        setCapturedImage(imageDataUrl);
    };

    const handleRetake = async () => {
        setCapturedImage(null);
        // Wait for state update to complete
        await new Promise(resolve => setTimeout(resolve, 0));

        try {
            const mediaStream = await navigator.mediaDevices.getUserMedia({
                video: {
                    width: { ideal: 1280 },
                    height: { ideal: 720 },
                    facingMode: "user"
                }
            });
            setStream(mediaStream);
            setHasPermission(true);

            // Ensure video element exists and is accessible
            if (videoRef.current) {
                videoRef.current.srcObject = mediaStream;
                videoRef.current.onloadedmetadata = () => {
                    videoRef.current.play();
                };
            } else {
                console.error("Video element not found");
                // Clean up the stream if we can't attach it
                mediaStream.getTracks().forEach(track => track.stop());
            }
        } catch (err) {
            console.error("Camera access error:", err);
            setError("Camera access denied or not available.");
            setHasPermission(false);
        }
    };

    const handleUpload = async () => {
        if (!capturedImage || uploadLoading) return;

        setUploadLoading(true);
        const base64String = capturedImage.split(',')[1];
        const token = getToken();
        const userId = getUserId();
        console.log("base64String", base64String);
        try {
            const response = await userprofileImage({ userId: userId, base64Image: base64String }, token);

            if (response) {
                setPreviewUrl(capturedImage);
                stopCamera();
                showSnackbar('Cover photo updated successfully!');
                dispatch(setUserDetails(response.data.userDetails));
            }
        } catch (error) {
            console.error('Error uploading photo:', error);
            showSnackbar('Failed to update cover photo. Please try again.', 'error');
        } finally {
            setUploadLoading(false);
        }
    };

    const takePhotoClick = () => {
        if (showCamera) {
            stopCamera();
        } else {
            setShowCamera(true);
        }
    };

    useEffect(() => {
        if (showCamera && videoRef.current) {
            startCamera();
        }
    }, [showCamera]);

    // Add effect to handle video element when it becomes available
    useEffect(() => {
        if (videoRef.current && stream && !capturedImage) {
            videoRef.current.srcObject = stream;
            videoRef.current.onloadedmetadata = () => {
                videoRef.current.play();
            };
        }
    }, [stream, capturedImage]);

    // Cleanup effect to stop camera when component unmounts
    useEffect(() => {
        return () => {
            if (stream) {
                stream.getTracks().forEach((track) => track.stop());
                setStream(null);
            }
        };
    }, [stream]);

    const handleFileSelect = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // Reset input value so user can re-select the same file if needed
        event.target.value = "";

        const validTypes = ["image/jpeg", "image/png", "image/webp", "image/jpg"];
        const isHeic = file.name.toLowerCase().endsWith(".heic") || file.name.toLowerCase().endsWith(".heif") || file.type === "image/heic" || file.type === "image/heif";

        if (isHeic || !validTypes.includes(file.type)) {
            showSnackbar("Please choose a JPG or PNG image.", "warning");
            return;
        }

        setUploadLoading(true);
        setSelectedFile(file);
        const reader = new FileReader();
        reader.onloadend = async () => {
            const base64String = reader.result.split(',')[1];
            const token = getToken();
            const userId = getUserId();
            try {
                const response = await userprofileImage({ userId: userId, base64Image: base64String }, token);

                if (response) {
                    setPreviewUrl(response.data.userDetails.photos || reader.result);
                    setPreviewImgError(false);
                    showSnackbar('Cover photo updated successfully!');
                    dispatch(setUserDetails(response.data.userDetails));
                }
            } catch (error) {
                console.error('Error uploading cover photo:', error);
                showSnackbar('Failed to update cover photo. Please try again.', 'error');
            } finally {
                setUploadLoading(false);
            }
        };
        reader.readAsDataURL(file);
    };

    const handleSnackbarClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackbar({ ...snackbar, open: false });
    };

    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({
            open: true,
            message,
            severity
        });
    };

    return (
        <div className="EditCoverPhoto">
            <div className="editCoverPhotoContainer">
                <div className="imageContainer">
                    <div className="imageContainerText">
                        <span className="coverPhotoText">Cover Photo</span>
                    </div>
                    {showCamera ? (
                        <div className="coverPhoto">
                            {capturedImage ? (
                                <>
                                    <img src={capturedImage} alt="captured" className="coverVideo" />
                                    <div className="captureControls">
                                        <button onClick={handleRetake} className="captureButton" disabled={uploadLoading}>Retake</button>
                                        <button
                                            onClick={handleUpload}
                                            className="captureButton uploadButton"
                                            disabled={uploadLoading}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '8px'
                                            }}
                                        >
                                            {uploadLoading && <CircularProgress size={16} style={{ color: 'white' }} />}
                                            {uploadLoading ? '' : 'Upload'}
                                        </button>
                                    </div>
                                </>
                            ) :
                                (error ? (
                                    <div className="faceErrorContainer">
                                        <div className="faceErrorMessage">{error}</div>
                                    </div>
                                ) : <>
                                    <video
                                        ref={videoRef}
                                        autoPlay
                                        playsInline
                                        muted
                                        className="coverVideo"
                                        style={{ transform: 'scaleX(-1)' }}
                                    />
                                    <button onClick={capturePhoto} className="captureButton captureMain">
                                        Capture
                                    </button>
                                </>
                                )}
                        </div>
                    ) : (
                        previewUrl && !previewImgError ? (
                            <img
                                src={previewUrl}
                                alt="Cover Photo"
                                className="coverPhoto"
                                onError={() => setPreviewImgError(true)}
                            />
                        ) : (
                            <div
                                className="coverPhoto"
                                style={{
                                    backgroundColor: getBackgroundColor(getUserInitials(userDetails?.firstName, userDetails?.lastName)),
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: "white",
                                    fontWeight: "bold",
                                    fontSize: "64px",
                                    fontFamily: "Arial, sans-serif"
                                }}
                            >
                                {getUserInitials(userDetails?.firstName, userDetails?.lastName)}
                            </div>
                        )
                    )}
                </div>
                <label className="uploadContainer" style={{ cursor: 'pointer' }} onClick={stopCamera}>
                    <input
                        type="file"
                        accept="image/png, image/jpeg, image/jpg, image/webp"
                        onChange={handleFileSelect}
                        style={{ display: 'none' }}
                    />
                    <img src='/Assets/BasicDetails/uploadIcon.svg' alt='no-img-found' className="cameraIcon" />
                    <span className="uploadText">Upload from gallery</span>
                </label>
                <div className="uploadContainer" onClick={takePhotoClick}>
                    <img src='/Assets/BasicDetails/takePhoto.svg' alt='no-img-found' className="cameraIcon" />
                    <span className="uploadText">Take photo</span>
                </div>
            </div>
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </div>
    );
}

export default EditCoverPhoto;